import { Component, ElementRef, OnInit, Renderer2, ViewChild, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { OnboardingSelectors } from '../../../store/onboarding/onboarding.selectors';
import { gsap } from 'gsap';
import MorphSVGPlugin from 'gsap/MorphSVGPlugin';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface VectorData {
  id: string;
  d: string;
  fill: string;
  stroke: string;
  strokeWidth: string;
}

@Component({
  selector: 'app-morphing-svg',
  standalone: true,
  templateUrl: './morphing-svg.component.html',
  styleUrl: '../../../../static/styles/components/morphing-svg.scss',
})
export class MorphingSvgComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly renderer = inject(Renderer2);
  readonly http = inject(HttpClient);

  @ViewChild('vectorGraphic') vectorEl!: ElementRef<HTMLElement>;
  @ViewChild('nextGraphic') nextEl!: ElementRef<HTMLElement>;

  // Keep track of the current graphic to distinguish between an initial load and a state change
  private currentGraphic: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Only need to pass the plugin itself here
    gsap.registerPlugin(MorphSVGPlugin);
  }

  ngOnInit(): void {
    this.store
      .select(OnboardingSelectors.graphic)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newGraphic) => {
          if (!newGraphic) return;

          // Skip the default placeholder graphic - wait for the real one
          if (newGraphic === 'svg_default.svg' && !this.currentGraphic) {
            return;
          }

          if (!this.currentGraphic) {
            // Initial Page Load: Load the correct graphic immediately. No morph needed.
            this.currentGraphic = newGraphic;
            this.setVectorGraphic(newGraphic);
          } else if (this.currentGraphic !== newGraphic) {
            // Subsequent Route Navigation: A new graphic was requested. Trigger the morph.
            this.currentGraphic = newGraphic;
            this.setNextGraphic(newGraphic);
          }
        },
      });
  }

  setVectorGraphic(graphic: string) {
    // Fixed path to be absolute so deep nested routes (like employee/create-password) don't 404
    this.http.get(`assets/morphing_svgs/${graphic}`, { responseType: 'text' }).subscribe({
      next: (svg) => {
        this.renderer.setProperty(this.vectorEl.nativeElement, 'innerHTML', svg);
      },
    });
  }

  setNextGraphic(graphic: string) {
    this.http.get(`assets/morphing_svgs/${graphic}`, { responseType: 'text' }).subscribe({
      next: (svg) => {
        this.renderer.setProperty(this.nextEl.nativeElement, 'innerHTML', svg);
        this.morphSvg();
      },
    });
  }

  get paths(): HTMLCollection | undefined {
    return this.vectorEl.nativeElement.children[0]?.children[0]?.children;
  }

  morphSvg() {
    MorphSVGPlugin.convertToPath('rect');
    MorphSVGPlugin.convertToPath('circle');
    MorphSVGPlugin.convertToPath('ellipse');

    // Helper function to safely grab ONLY visual paths, ignoring <defs>
    const getValidPaths = (container: Element) => {
      return Array.from(container.querySelectorAll('path')).filter((p) => !p.closest('defs'));
    };

    const currentPaths = getValidPaths(this.vectorEl.nativeElement);
    if (!currentPaths.length) return;

    const nextPaths = getValidPaths(this.nextEl.nativeElement);
    if (!nextPaths.length) return;

    const fromGraphic: VectorData[] = [];
    const toGraphic: VectorData[] = [];

    // Map current paths
    currentPaths.forEach((path: Element, index: number) => {
      let pathId = path.getAttribute('id');
      if (!pathId) {
        pathId = `auto_from_${index}_${Date.now()}`;
        path.setAttribute('id', pathId);
      }

      fromGraphic.push({
        id: pathId,
        d: path.getAttribute('d') ?? '',
        fill: path.getAttribute('fill') ?? '',
        stroke: path.getAttribute('stroke') ?? path.getAttribute('fill') ?? '',
        strokeWidth: path.getAttribute('stroke-width') ?? '0.1',
      });
    });

    // Map destination paths
    nextPaths.forEach((path: Element, index: number) => {
      let pathId = path.getAttribute('id');
      if (!pathId) {
        pathId = `auto_to_${index}_${Date.now()}`;
        path.setAttribute('id', pathId);
      }

      toGraphic.push({
        id: pathId,
        d: path.getAttribute('d') ?? '',
        fill: path.getAttribute('fill') ?? '',
        stroke: path.getAttribute('stroke') ?? path.getAttribute('fill') ?? '',
        strokeWidth: path.getAttribute('stroke-width') ?? '0.1',
      });
    });

    const largest = Math.max(fromGraphic.length, toGraphic.length);
    let count = 1;

    // The Timeline Manager
    const tl = gsap.timeline({
      onComplete: () => {
        this.renderer.setProperty(this.vectorEl.nativeElement, 'innerHTML', this.nextEl.nativeElement.innerHTML);
      },
    });

    for (let i = 0; i < largest; i++) {
      if (!fromGraphic[i]) {
        tl.to(
          `#${fromGraphic[i - count].id}`,
          {
            duration: 0.4,
            morphSVG: `#${toGraphic[i].id}`,
            fill: toGraphic[i].fill,
            stroke: toGraphic[i].stroke,
            strokeWidth: toGraphic[i].strokeWidth,
          },
          0,
        );
        count++;
      } else if (toGraphic[i]) {
        // 1-to-1 morph
        tl.to(
          `#${fromGraphic[i].id}`,
          {
            duration: 0.4,
            morphSVG: `#${toGraphic[i].id}`,
            fill: toGraphic[i].fill,
            stroke: toGraphic[i].stroke,
            strokeWidth: toGraphic[i].strokeWidth,
          },
          0,
        );
      } else {
        tl.to(
          `#${fromGraphic[i].id}`,
          {
            duration: 0.4,
            morphSVG: `#${toGraphic[i - count].id}`,
            fill: toGraphic[i - count].fill,
            stroke: toGraphic[i - count].stroke,
            strokeWidth: toGraphic[i - count].strokeWidth,
          },
          0,
        );
        count++;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
